import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  DatePicker,
  message,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  Tag,
  List,
  Avatar,
  Typography,
  Badge,
  Divider,
  Checkbox,
} from "antd";
import {
  FilterOutlined,
  UserOutlined,
  TeamOutlined,
  PhoneOutlined,
  CalendarOutlined,
  MessageOutlined,
  BellOutlined,
  EyeOutlined,
  CloseOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import moment from "moment";
import api from "../../../services/api";
import CommonSelect from "../../../core/common/commonSelect";
import { Dayjs } from "dayjs";
import Table from "../../../core/common/dataTable/index";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface CallLog {
  type: string;
  date: string;
  note?: string;
  reminder_date?: string;
  added_by?: number | string | null;
  official_contact_number?: string | null;
}

interface Lead {
  id: string | number;
  name: string;
  email: string;
  mobileNumber: string;
  address: string;
  country: string;
  state: string;
  district: string;
  city: string;
  status?: string;
  createdAt?: string;
  userIds?: number[];
  call_logs?: CallLog[];
  assigned_official_contact_numbers?: string[];
}

interface User {
  key: number;
  fname: string;
  lname: string;
  email: string;
  profilePic?: string;
  companyId: number;
  companyType: string;
  officialPhoneCode?: string;
  officialMobileNo?: string;
}

interface Company {
  id: string;
  name: string;
  type: string;
  isPrimary?: boolean;
}

interface LeadForm {
  key: number;
  id: number;
  LeadFormName: string;
  pipeline_id: number;
  employees: number[];
}

// Define call type colors and icons for better visualization
// prettier-ignore
const CALL_TYPE_CONFIG: Record<
  string,
  { color: string; icon: React.ReactNode }
> = {
  "Reference": { color: "#52c41a", icon: <TeamOutlined /> },
  "Call Later": { color: "#1890ff", icon: <CalendarOutlined /> },
  "Not Reachable/Switched Off": { color: "#faad14", icon: <PhoneOutlined /> },
  "Not Picked": { color: "#fa8c16", icon: <PhoneOutlined /> },
  "Not Interested": { color: "#f5222d", icon: <CloseOutlined /> },
  "Lead/Deal Closed": { color: "#722ed1", icon: <BellOutlined /> },
  "Closed by us": { color: "#eb2f96", icon: <BellOutlined /> },
  "Closed outside": { color: "#a0d911", icon: <BellOutlined /> },
  "Invalid/Wrong Number": { color: "#fa541c", icon: <CloseOutlined /> },
  // Default configuration for unknown types
  "default": { color: "#d9d9d9", icon: <PhoneOutlined /> },
};

const EmployeeUserCall: React.FC = () => {
  const user = useSelector((state: any) => state.user.user);
  const role = user?.userDetails?.role_char?.toLowerCase();
  const isEmployee = role === "employee";
  // The endpoint middleware only lets admin / employeeadmin pull data for more
  // than one user. Everyone else can only read their own leads.
  const isPrivileged = role === "admin" || role === "employeeadmin";

  const getSignature = (): string | null => localStorage.getItem("signature");

  var initialFormDataChooseForm = {
    signature: getSignature(),
    id: "Select Company",
    name: "",
    type: "",
  };

  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState(
    initialFormDataChooseForm,
  );

  const [formList, setFormList] = useState<LeadForm[]>([]);
  const [selectedForm, setSelectedForm] = useState({
    form_id: "",
    form_name: "Select Form",
    pipeline_id: "",
    employees: [] as number[],
  });
  // The user picked within the chosen form ("all" => every form member)
  const [selectedFormUser, setSelectedFormUser] = useState<{
    id: string | number;
    name: string;
  }>({ id: "all", name: "All Users" });

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    unassigned: 0,
    withCalls: 0,
  });
  const [callTypeStats, setCallTypeStats] = useState<Record<string, number>>(
    {},
  );
  const [isCallLogModalVisible, setIsCallLogModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedCallTypeFilter, setSelectedCallTypeFilter] = useState<
    string | null
  >(null);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);

  // Export modal (select which form members to include in the export)
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [selectedExportUserIds, setSelectedExportUserIds] = useState<number[]>(
    [],
  );
  const [exportUserSelectAll, setExportUserSelectAll] = useState(true);

  const withSignature = <T extends object>(
    data?: T,
  ): T & { signature: string | null } => ({
    ...(data || ({} as T)),
    signature: getSignature(),
  });

  // The form members that we can map to a real user (name + avatar).
  const formUserOptions = filteredUsers.filter((u) =>
    (selectedForm.employees || []).map(Number).includes(Number(u.key)),
  );

  // Options for the form-user dropdown: "All Users" + each form member.
  const formUserChoose = [
    { value: "all", label: "All Users" },
    ...formUserOptions.map((u) => ({
      value: String(u.key),
      label: `${u.fname} ${u.lname}`,
    })),
  ];

  // Resolve which employee ids should drive the lead fetch.
  const getActiveEmployeeIds = (): number[] => {
    if (!isPrivileged) {
      // Non-admins can only ever read their own leads.
      const ownId = Number(user?.userDetails?.id);
      return ownId ? [ownId] : [];
    }
    // A specific form member is selected -> just that user.
    if (selectedFormUser.id && selectedFormUser.id !== "all") {
      return [Number(selectedFormUser.id)];
    }
    // Otherwise every member of the selected form.
    return (selectedForm.employees || []).map(Number).filter(Boolean);
  };

  // Calculate call type statistics
  const calculateCallTypeStats = (leadsData: Lead[]) => {
    const callTypeCounts: Record<string, number> = {};

    leadsData.forEach((lead) => {
      if (lead.call_logs && Array.isArray(lead.call_logs)) {
        lead.call_logs.forEach((log) => {
          const callType = log.type || "Unknown";
          callTypeCounts[callType] = (callTypeCounts[callType] || 0) + 1;
        });
      }
    });

    setCallTypeStats(callTypeCounts);
  };

  // Fetch company list
  const fetchCompanyList = async () => {
    try {
      const response = await api.post("userService/getAllCompanyList", {
        signature: getSignature(),
      });
      const companies = response.data.data.data;
      setCompanyList(companies);

      const newFormData = {
        id: companies[0].id,
        name: companies[0].name,
        type: companies[0].type,
      };
      setSelectedCompany((prev: any) => ({
        ...prev,
        ...newFormData,
      }));

      const primaryCompany = companies[0];

      // Fetch users for the primary company
      await fetchAllUsers(primaryCompany);
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error("Failed to fetch companies");
    }
  };

  const companyChoose = [
    ...companyList?.map((company: any) => ({
      value: company.id,
      label: company.name + " - " + company.type,
    })),
  ];

  // Fetch all users for a company
  const fetchAllUsers = async (company: Company | null) => {
    if (!company) {
      setFilteredUsers([]);
      return;
    }

    try {
      const response = await api.post(
        "userService/getAllEmployeesListDataByCompanyId",
        withSignature({
          companyId: company.id,
          companyType: company.type,
        }),
      );

      const userData = response.data?.data?.data?.allUsers || [];
      const mappedUsers = userData.map((user: any) => ({
        key: user.id,
        fname: user.fname,
        lname: user.lname,
        email: user.email,
        profilePic: user.profile_pic,
        companyId: user.company_id,
        companyType: user.company_type,
        officialPhoneCode: user.official_phone_code,
        officialMobileNo: user.official_mobile_no,
      }));

      let finalUsers = mappedUsers;
      if (isEmployee) {
        finalUsers = mappedUsers.filter(
          (u: any) => u.key == user?.userDetails?.id,
        );
      }
      setFilteredUsers(finalUsers);
    } catch (error) {
      message.error("Failed to fetch users");
      console.error("Error fetching users:", error);
    }
  };

  // Fetch the lead forms for a company (each form carries its member ids)
  const fetchForms = async (company: { id: any; type: any } | null) => {
    if (!company?.id) {
      return;
    }
    try {
      const response = await api.post(
        "leadService/getLeadFormsByCompany",
        withSignature({
          company_id: company.id,
          company_type: company.type,
        }),
      );

      const forms: LeadForm[] = response.data?.data?.data || [];
      setFormList(forms);
      // Reset the user picker whenever the form set changes.
      setSelectedFormUser({ id: "all", name: "All Users" });

      if (forms.length > 0) {
        const firstForm = forms[0];
        setSelectedForm({
          form_id: firstForm.key?.toString() ?? "",
          form_name: firstForm.LeadFormName,
          pipeline_id: firstForm.pipeline_id?.toString() ?? "",
          employees: Array.isArray(firstForm.employees)
            ? firstForm.employees
            : [],
        });
      } else {
        setSelectedForm({
          form_id: "",
          form_name: "No Forms Available",
          pipeline_id: "",
          employees: [],
        });
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
      setFormList([]);
      setSelectedFormUser({ id: "all", name: "All Users" });
      setSelectedForm({
        form_id: "",
        form_name: "Error Loading Forms",
        pipeline_id: "",
        employees: [],
      });
    }
  };

  const formChoose = [
    ...formList?.map((form: LeadForm) => ({
      value: `${form.pipeline_id}-${form.key}`,
      label: form.LeadFormName,
    })),
  ];

  // Handle company change
  const handleCompanyChange = async (value: any) => {
    const newFormData = {
      id: value.value,
      name: value.label.split("-")[0].trim(),
      type: value.label.split("-")[1].trim(),
    };
    const company = companyList.find(
      (c) =>
        parseInt(c.id) === parseInt(newFormData.id) &&
        c.type?.toLowerCase() === newFormData.type?.toLowerCase(),
    );
    if (company) {
      setSelectedCompany((prev: any) => ({
        ...prev,
        ...newFormData,
      }));
      setDateRange(null);

      // Clear previously fetched results until the new filters are applied
      setLeads([]);
      setFilteredLeads([]);
      setStats({ total: 0, assigned: 0, unassigned: 0, withCalls: 0 });
      setCallTypeStats({});
      setSelectedCallTypeFilter(null);

      await fetchAllUsers(company);
      // Forms are refetched by the effect watching selectedCompany.
    }
  };

  const handleFormChange = (value: any) => {
    if (!value) return;
    const [pipeline_id, form_id] = value.value.split("-");
    const form = formList.find((f) => f.key === parseInt(form_id));
    setSelectedForm({
      form_id,
      form_name: value.label,
      pipeline_id,
      employees: Array.isArray(form?.employees) ? form!.employees : [],
    });
    // Reset the user picker to "All Users" for the newly selected form.
    setSelectedFormUser({ id: "all", name: "All Users" });
  };

  const handleFormUserChange = (value: any) => {
    if (!value) return;
    setSelectedFormUser({ id: value.value, name: value.label });
  };

  // Fetch leads based on filters
  const fetchLeadsByEmployeeAndDate = async () => {
    if (!selectedCompany) {
      message.warning("Please select a company first");
      return;
    }

    if (!selectedForm.form_id) {
      message.warning("Please select a form");
      return;
    }

    const employeeIds = getActiveEmployeeIds();
    if (employeeIds.length === 0) {
      message.warning("No users are associated with this form");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        employeeIds,
        companyId: selectedCompany.id,
        companyType: selectedCompany.type,
      };

      if (dateRange) {
        payload.startDate = dateRange[0].format("YYYY-MM-DD");
        payload.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await api.post(
        "callLogsService/get-leads-by-employee-date",
        withSignature(payload),
      );

      const leadsData = response.data?.data || [];
      setLeads(leadsData);
      setFilteredLeads(leadsData);

      // Calculate statistics
      const assigned = leadsData.filter(
        (lead: Lead) => lead.status === "assigned",
      ).length;
      const withCalls = leadsData.filter(
        (lead: Lead) => lead.call_logs && lead.call_logs.length > 0,
      ).length;

      setStats({
        total: leadsData.length,
        assigned,
        unassigned: leadsData.length - assigned,
        withCalls,
      });

      // Calculate call type statistics
      calculateCallTypeStats(leadsData);

      message.success(`Found ${leadsData.length} leads`);
    } catch (error) {
      console.error("Error fetching leads:", error);
      message.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCallTypeFilter) {
      const filtered = leads.filter((lead) => {
        if (
          !lead.call_logs ||
          !Array.isArray(lead.call_logs) ||
          lead.call_logs.length === 0
        ) {
          return false;
        }

        const sortedCallLogs = [...lead.call_logs].sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });

        const latestCallLog = sortedCallLogs[0];
        return latestCallLog?.type === selectedCallTypeFilter;
      });
      setFilteredLeads(filtered);
    } else {
      setFilteredLeads(leads);
    }
  }, [selectedCallTypeFilter, leads]);

  useEffect(() => {
    fetchCompanyList();
  }, []);

  // Refetch forms whenever a real company is selected
  useEffect(() => {
    if (
      selectedCompany &&
      selectedCompany.id &&
      selectedCompany.id !== "Select Company"
    ) {
      fetchForms(selectedCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const handleDateChange = (dates: any) => {
    setDateRange(dates);
  };

  // Function to show call log details in modal
  const showCallLogs = (lead: Lead) => {
    setSelectedLead(lead);
    setIsCallLogModalVisible(true);
  };

  // Function to close the modal
  const handleCancel = () => {
    setIsCallLogModalVisible(false);
    setSelectedLead(null);
  };

  // Get configuration for call type
  const getCallTypeConfig = (callType: string) => {
    return CALL_TYPE_CONFIG[callType] || CALL_TYPE_CONFIG.default;
  };

  // Build the export dataset (and its summary) honouring the user selection.
  const buildExportContext = () => {
    const selectedLeads =
      exportUserSelectAll || selectedExportUserIds.length === 0
        ? leads
        : leads.filter(
            (lead) =>
              Array.isArray(lead.userIds) &&
              lead.userIds.some((id) =>
                selectedExportUserIds.includes(Number(id)),
              ),
          );
    // Keep the export grouped by user, same as the on-screen table.
    const exportLeads = sortLeadsByAssignedUser(selectedLeads);

    const assigned = exportLeads.filter(
      (lead) => lead.status === "assigned",
    ).length;
    const withCalls = exportLeads.filter(
      (lead) => lead.call_logs && lead.call_logs.length > 0,
    ).length;

    const summary = {
      total: exportLeads.length,
      assigned,
      unassigned: exportLeads.length - assigned,
      withCalls,
    };

    const typeCounts: Record<string, number> = {};
    exportLeads.forEach((lead) => {
      (lead.call_logs || []).forEach((log) => {
        const t = log.type || "Unknown";
        typeCounts[t] = (typeCounts[t] || 0) + 1;
      });
    });

    return { exportLeads, summary, typeCounts };
  };

  // Export to PDF
  const handleExportPDF = () => {
    setExportLoading(true);
    try {
      const { exportLeads, summary, typeCounts } = buildExportContext();
      const doc = new jsPDF("landscape");

      // Add title
      doc.setFontSize(16);
      doc.text("Employee Calls Data Report", 14, 15);

      // Add filters information
      doc.setFontSize(10);
      doc.text(
        `Company: ${selectedCompany.name} - ${selectedCompany.type}`,
        14,
        25,
      );
      doc.text(`Form: ${selectedForm.form_name}`, 14, 32);

      if (dateRange) {
        doc.text(
          `Date Range: ${dateRange[0].format(
            "DD MMM YYYY",
          )} - ${dateRange[1].format("DD MMM YYYY")}`,
          14,
          39,
        );
      }

      doc.text(`Generated on: ${moment().format("DD MMM YYYY HH:mm")}`, 14, 46);

      // Statistics section
      doc.setFontSize(12);
      doc.text("Summary Statistics", 14, 60);

      const statsData = [
        ["Total Leads", summary.total.toString()],
        ["Assigned", summary.assigned.toString()],
        ["Unassigned", summary.unassigned.toString()],
        ["With Calls", summary.withCalls.toString()],
      ];

      (doc as any).autoTable({
        startY: 65,
        head: [["Metric", "Count"]],
        body: statsData,
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 135, 245] },
      });

      // Call type statistics
      if (Object.keys(typeCounts).length > 0) {
        const callTypeData = Object.entries(typeCounts).map(([type, count]) => [
          type,
          count.toString(),
        ]);

        (doc as any).autoTable({
          startY: (doc as any).lastAutoTable.finalY + 10,
          head: [["Call Type", "Count"]],
          body: callTypeData,
          theme: "grid",
          styles: { fontSize: 9 },
          headStyles: { fillColor: [102, 187, 106] },
        });
      }

      // Main data table with call logs
      const tableData = exportLeads.map((lead, index) => {
        // Format call log details
        let callLogDetails = "No call logs";
        if (
          lead.call_logs &&
          Array.isArray(lead.call_logs) &&
          lead.call_logs.length > 0
        ) {
          callLogDetails = lead.call_logs
            .map((log) => {
              const dateStr = log.date
                ? moment(log.date).format("DD MMM YYYY HH:mm")
                : "No date";
              return `${log.type || "Unknown"}: ${dateStr}${
                log.note ? ` (${log.note})` : ""
              }`;
            })
            .join("\n");
        }

        return [
          (index + 1).toString(),
          lead.name || "N/A",
          lead.email || "N/A",
          lead.mobileNumber || "N/A",
          [lead.city, lead.district, lead.state, lead.country]
            .filter(Boolean)
            .join(", ") || "N/A",
          lead.status || "Unassigned",
          getLeadOfficialNumbers(lead),
          (lead.call_logs?.length || 0).toString(),
          callLogDetails,
          lead.createdAt ? moment(lead.createdAt).format("DD MMM YYYY") : "N/A",
        ];
      });

      (doc as any).autoTable({
        startY: (doc as any).lastAutoTable.finalY + 10,
        head: [
          [
            "S.No",
            "Name",
            "Email",
            "Phone",
            "Location",
            "Status",
            "Official Contact Number",
            "Call Logs Count",
            "Call Log Details",
            "Created Date",
          ],
        ],
        body: tableData,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: "linebreak",
          cellWidth: "wrap",
        },
        columnStyles: {
          0: { cellWidth: 10 }, // S.No
          1: { cellWidth: 20 }, // Name
          2: { cellWidth: 25 }, // Email
          3: { cellWidth: 15 }, // Phone
          4: { cellWidth: 25 }, // Location
          5: { cellWidth: 12 }, // Status
          6: { cellWidth: 25 }, // Official Contact Number
          7: { cellWidth: 12 }, // Call Logs Count
          8: { cellWidth: 40 }, // Call Log Details (wider for details)
          9: { cellWidth: 15 }, // Created Date
        },
        headStyles: {
          fillColor: [41, 128, 185],
          fontSize: 8,
        },
        pageBreak: "auto",
        margin: { horizontal: 5 },
        didDrawPage: function (data: any) {
          // Add page numbers
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber}`,
            data.settings.margin.left,
            doc.internal.pageSize.height - 10,
          );
        },
      });

      doc.save(`employee-calls-${moment().format("YYYY-MM-DD-HH-mm")}.pdf`);
      message.success("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      message.error("Failed to export PDF");
    } finally {
      setExportLoading(false);
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    setExportLoading(true);
    try {
      const { exportLeads, summary, typeCounts } = buildExportContext();
      let rowIndex = 0;
      const excelData: any[] = [];

      exportLeads.forEach((lead) => {
        if (
          !lead.call_logs ||
          !Array.isArray(lead.call_logs) ||
          lead.call_logs.length === 0
        ) {
          excelData.push({
            "S.No": ++rowIndex,
            "Lead ID": lead.id || "N/A",
            Name: lead.name || "N/A",
            Email: lead.email || "N/A",
            Phone: lead.mobileNumber || "N/A",
            City: lead.city || "N/A",
            District: lead.district || "N/A",
            State: lead.state || "N/A",
            Country: lead.country || "N/A",
            Status: lead.status || "Unassigned",
            "Assigned User": getAssignedUserNames(lead).join(", ") || "—",
            "Call Number": "No calls",
            "Call Date": "N/A",
            "Call Time": "N/A",
            "Call Type": "N/A",
            "Official Contact Number": getCallOfficialNumber(null, lead),
            "Call Note": "N/A",
            "Created Date": lead.createdAt
              ? moment(lead.createdAt).format("DD MMM YYYY")
              : "N/A",
          });
        } else {
          // Sort call logs by date (newest first)
          const sortedCallLogs = [...lead.call_logs]
            .filter((log) => log.date)
            .sort((a, b) => {
              const dateA = new Date(a.date!).getTime();
              const dateB = new Date(b.date!).getTime();
              return dateB - dateA; // Descending (newest first)
            });

          // Create one row for each call log
          sortedCallLogs.forEach((log, callIndex) => {
            const dateStr = log.date
              ? moment(log.date).format("DD MMM YYYY")
              : "No date";
            const timeStr = log.date
              ? moment(log.date).format("HH:mm")
              : "No time";
            const callType = log.type || "Unknown";

            excelData.push({
              "S.No": ++rowIndex,
              "Lead ID": lead.id || "N/A",
              Name: lead.name || "N/A",
              Email: lead.email || "N/A",
              Phone: lead.mobileNumber || "N/A",
              City: lead.city || "N/A",
              District: lead.district || "N/A",
              State: lead.state || "N/A",
              Country: lead.country || "N/A",
              Status: lead.status || "Unassigned",
              "Assigned User": getAssignedUserNames(lead).join(", ") || "—",
              "Call Number": `${callIndex + 1}/${sortedCallLogs.length}`,
              "Call Date": dateStr,
              "Call Time": timeStr,
              "Call Type": callType,
              "Official Contact Number": getCallOfficialNumber(log, lead),
              "Call Note": log.note || "N/A",
              "Created Date": lead.createdAt
                ? moment(lead.createdAt).format("DD MMM YYYY")
                : "N/A",
            });
          });
        }
      });

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Main data sheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(wb, ws, "Leads Data");

      // Adjust column widths for better readability
      const wscols = [
        { wch: 5 }, // S.No
        { wch: 8 }, // Lead ID
        { wch: 20 }, // Name
        { wch: 25 }, // Email
        { wch: 15 }, // Phone
        { wch: 15 }, // City
        { wch: 15 }, // District
        { wch: 15 }, // State
        { wch: 15 }, // Country
        { wch: 12 }, // Status
        { wch: 25 }, // Assigned User
        { wch: 10 }, // Call Number (e.g., "1/5")
        { wch: 15 }, // Call Date
        { wch: 10 }, // Call Time
        { wch: 20 }, // Call Type
        { wch: 22 }, // Official Contact Number
        { wch: 30 }, // Call Note
        { wch: 15 }, // Created Date
      ];
      ws["!cols"] = wscols;

      // Statistics sheet
      const statsData = [
        { Metric: "Total Leads", Count: summary.total },
        { Metric: "Assigned", Count: summary.assigned },
        { Metric: "Unassigned", Count: summary.unassigned },
        { Metric: "With Calls", Count: summary.withCalls },
      ];

      // Add call type statistics
      Object.entries(typeCounts).forEach(([type, count]) => {
        statsData.push({ Metric: `Call Type - ${type}`, Count: count });
      });

      const statsWs = XLSX.utils.json_to_sheet(statsData);
      XLSX.utils.book_append_sheet(wb, statsWs, "Statistics");

      // Save file
      XLSX.writeFile(
        wb,
        `employee-calls-detailed-${moment().format("YYYY-MM-DD-HH-mm")}.xlsx`,
      );
      message.success("Excel exported successfully");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      message.error("Failed to export Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const openExportModal = () => {
    setSelectedExportUserIds([]);
    setExportUserSelectAll(true);
    setIsExportModalVisible(true);
  };

  // Map user id -> display name so each lead can show who it belongs to.
  const userMap = new Map<number, string>();
  filteredUsers.forEach((u) =>
    userMap.set(Number(u.key), `${u.fname} ${u.lname}`.trim()),
  );

  const officialNumberMap = new Map<number, string>();
  filteredUsers.forEach((u) => {
    const number = String(u.officialMobileNo ?? "").trim();
    if (!number || number === "null" || number === "undefined") return;
    const code = String(u.officialPhoneCode ?? "").trim();
    const isUsableCode = code && code !== "null" && code !== "undefined";
    officialNumberMap.set(
      Number(u.key),
      isUsableCode ? `${code}-${number}` : number,
    );
  });

  const getAssignedOfficialNumbers = (lead: Lead): string[] => {
    const fromApi = Array.isArray(lead.assigned_official_contact_numbers)
      ? lead.assigned_official_contact_numbers.filter(Boolean)
      : [];
    if (fromApi.length > 0) return Array.from(new Set(fromApi));

    const ids = Array.isArray(lead.userIds) ? lead.userIds : [];
    return Array.from(
      new Set(
        ids
          .map((id) => officialNumberMap.get(Number(id)))
          .filter((value): value is string => Boolean(value)),
      ),
    );
  };

  const getCallOfficialNumber = (log: CallLog | null, lead: Lead): string => {
    const fromApi = String(log?.official_contact_number ?? "").trim();
    if (fromApi) return fromApi;

    const addedBy = Number(log?.added_by);
    if (Number.isInteger(addedBy) && addedBy > 0) {
      const byCall = officialNumberMap.get(addedBy);
      if (byCall) return byCall;
    }

    const assigned = getAssignedOfficialNumbers(lead);
    return assigned.length > 0 ? assigned.join(", ") : "N/A";
  };

  const getLeadOfficialNumbers = (lead: Lead): string => {
    const logs = lead.call_logs || [];
    const numbers = (
      logs.length > 0
        ? logs.map((log) => getCallOfficialNumber(log, lead))
        : [getCallOfficialNumber(null, lead)]
    ).filter((value) => value !== "N/A");
    const unique = Array.from(new Set(numbers));
    return unique.length > 0 ? unique.join(", ") : "N/A";
  };

  const getAssignedUserNames = (lead: Lead): string[] => {
    const ids = Array.isArray(lead.userIds) ? lead.userIds : [];
    // Only show resolvable real names; ignore ids we can't map to a user.
    return ids
      .map((id) => userMap.get(Number(id)))
      .filter((name): name is string => Boolean(name));
  };

  // Group rows so a given user's leads stay contiguous (not interleaved).
  const assignedSortKey = (lead: Lead): string =>
    getAssignedUserNames(lead)
      .slice()
      .sort((a, b) => a.localeCompare(b))
      .join(", ")
      .toLowerCase();

  const sortLeadsByAssignedUser = (rows: Lead[]): Lead[] =>
    [...rows].sort((a, b) => {
      const ka = assignedSortKey(a);
      const kb = assignedSortKey(b);
      if (ka === kb) return 0;
      if (!ka) return 1; // rows without a resolvable user go last
      if (!kb) return -1;
      return ka.localeCompare(kb);
    });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => name || "N/A",
      sorter: (a: Lead, b: Lead) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => email || "N/A",
      sorter: (a: Lead, b: Lead) =>
        (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Phone",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      render: (phone: string) => (
        <div>
          <PhoneOutlined /> {phone || "null"}
        </div>
      ),
      sorter: (a: Lead, b: Lead) =>
        Number(a.mobileNumber) - Number(b.mobileNumber),
    },
    {
      title: "Location",
      key: "location",
      render: (record: Lead) => {
        const locationParts = [
          record.city,
          record.district,
          record.state,
          record.country,
        ].filter(Boolean);

        return (
          <span>
            {locationParts.length > 0 ? locationParts.join(", ") : "null"}
          </span>
        );
      },
      sorter: (a: Lead, b: Lead) => {
        const locationA = [a.city, a.district, a.state, a.country]
          .filter(Boolean)
          .join(", ");
        const locationB = [b.city, b.district, b.state, b.country]
          .filter(Boolean)
          .join(", ");
        return locationA.localeCompare(locationB);
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "assigned" ? "green" : "orange"}>
          {status || "Unassigned"}
        </Tag>
      ),
    },
    {
      title: "Assigned To",
      key: "assignedTo",
      render: (record: Lead) => {
        const names = getAssignedUserNames(record);
        if (names.length === 0) {
          return <span style={{ color: "#999" }}>—</span>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {names.map((name, i) => (
              <Tag key={i} color="blue" style={{ marginInlineEnd: 0 }}>
                {name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Call Logs",
      dataIndex: "call_logs",
      key: "call_logs",
      render: (logs: CallLog[], record: Lead) => {
        const logCount = logs?.length || 0;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Badge count={logCount} showZero={false} />
            {logCount > 0 ? (
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => showCallLogs(record)}
                style={{ marginLeft: 8 }}
              >
                View
              </Button>
            ) : (
              <span style={{ marginLeft: 8, color: "#999" }}></span>
            )}
          </div>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) =>
        date ? moment(date).format("DD MMM YYYY") : "null",
      sorter: (a: Lead, b: Lead) =>
        moment(a.createdAt || 0).unix() - moment(b.createdAt || 0).unix(),
    },
  ];

  return (
    <div
      className="page-wrapper"
      style={{ paddingTop: "64px", minHeight: "100vh" }}
    >
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Employee Calls Data</h4>
            <h6>View calls data filtered by form and date range</h6>
          </div>
          {!isEmployee && (
            <div className="page-btn">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                disabled={leads.length === 0}
                onClick={openExportModal}
              >
                Export Data
              </Button>
            </div>
          )}
        </div>

        {/* Filter Card */}
        <Card title="Filters" style={{ marginBottom: 20 }}>
          <Row
            gutter={[16, 16]}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Col xs={24} sm={12} lg={5}>
              <div className="form-group">
                <label>Company</label>
                <CommonSelect
                  className="select"
                  options={companyChoose}
                  defaultValue={{
                    value: selectedCompany?.id + "-" + selectedCompany?.type,
                    label: selectedCompany?.name + "-" + selectedCompany?.type,
                  }}
                  onChange={handleCompanyChange}
                />
              </div>
            </Col>

            <Col xs={24} sm={12} lg={5}>
              <div className="form-group">
                <label>Form</label>
                <CommonSelect
                  className="select"
                  options={formChoose}
                  defaultValue={{
                    value: selectedForm.pipeline_id
                      ? `${selectedForm.pipeline_id}-${selectedForm.form_id}`
                      : "",
                    label: selectedForm.form_name,
                  }}
                  onChange={handleFormChange}
                />
              </div>
            </Col>

            <Col xs={24} sm={12} lg={5}>
              <div className="form-group">
                <label>User</label>
                <CommonSelect
                  className="select"
                  options={formUserChoose}
                  defaultValue={{
                    value: String(selectedFormUser.id),
                    label: selectedFormUser.name,
                  }}
                  onChange={handleFormUserChange}
                  isDisabled={!selectedForm.form_id}
                />
              </div>
            </Col>

            <Col xs={24} sm={12} lg={5}>
              <div className="form-group" style={{ marginTop: "9px" }}>
                <label>Date Range </label>
                <RangePicker
                  onChange={handleDateChange}
                  value={dateRange}
                  style={{ width: "100%" }}
                  disabled={!selectedCompany}
                />
              </div>
            </Col>

            <Col xs={24} sm={24} lg={4}>
              <div className="form-group" style={{ marginTop: "25px" }}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={fetchLeadsByEmployeeAndDate}
                  loading={loading}
                  disabled={!selectedCompany || !selectedForm.form_id}
                >
                  Apply Filters
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Basic Statistics */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Assigned"
                value={stats.assigned}
                valueStyle={{ color: "#3f8600" }}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="With Calls"
                value={stats.withCalls}
                prefix={<PhoneOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Dynamic Call Type Statistics */}
        {Object.keys(callTypeStats).length > 0 && (
          <>
            <Title level={4} style={{ marginBottom: 16 }}>
              Call Logs Type Breakdown
              {selectedCallTypeFilter && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => setSelectedCallTypeFilter(null)}
                  style={{ marginLeft: 16, fontSize: 12 }}
                >
                  Clear Filter
                </Button>
              )}
            </Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              {Object.entries(callTypeStats).map(([callType, count]) => {
                const config = getCallTypeConfig(callType);
                const isSelected = selectedCallTypeFilter === callType;

                const filteredCount = leads.filter((lead) => {
                  if (
                    !lead.call_logs ||
                    !Array.isArray(lead.call_logs) ||
                    lead.call_logs.length === 0
                  ) {
                    return false;
                  }
                  const sortedCallLogs = [...lead.call_logs].sort((a, b) => {
                    const dateA = a.date ? new Date(a.date).getTime() : 0;
                    const dateB = b.date ? new Date(b.date).getTime() : 0;
                    return dateB - dateA;
                  });
                  const latestCallLog = sortedCallLogs[0];
                  return latestCallLog?.type === callType;
                }).length;

                return (
                  <Col xs={12} sm={8} md={6} lg={4} key={callType}>
                    <Card
                      size="small"
                      hoverable
                      onClick={() => setSelectedCallTypeFilter(callType)}
                      style={{
                        cursor: "pointer",
                        border: isSelected
                          ? `2px solid ${config.color}`
                          : "1px solid #f0f0f0",
                        boxShadow: isSelected
                          ? `0 0 0 2px ${config.color}33`
                          : "none",
                        transition: "all 0.3s",
                      }}
                    >
                      <Statistic
                        title={
                          <span
                            style={{
                              fontSize: "12px",
                              wordBreak: "break-word",
                            }}
                          >
                            {callType}
                          </span>
                        }
                        value={isSelected ? filteredCount : count}
                        valueStyle={{
                          color: config.color,
                          fontWeight: isSelected ? "bold" : "normal",
                        }}
                        prefix={config.icon}
                      />
                      <div
                        style={{ fontSize: 10, color: "#666", marginTop: 4 }}
                      >
                        {isSelected
                          ? `${filteredCount} leads filtered`
                          : `${count} total calls`}
                      </div>
                      {isSelected && (
                        <div
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            fontSize: 12,
                            color: config.color,
                          }}
                        >
                          ✓
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Add this section to show the filter summary */}
            {selectedCallTypeFilter && (
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  backgroundColor: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 4,
                }}
              >
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text strong>
                      Showing {filteredLeads.length} leads with latest call
                      type: "
                      <span
                        style={{
                          color: getCallTypeConfig(selectedCallTypeFilter)
                            .color,
                        }}
                      >
                        {selectedCallTypeFilter}
                      </span>
                      "
                    </Text>
                  </Col>
                  <Col>
                    <Button
                      size="small"
                      onClick={() => setSelectedCallTypeFilter(null)}
                    >
                      Clear Filter
                    </Button>
                  </Col>
                </Row>
              </div>
            )}
          </>
        )}
        {/* Results Table */}
        <div className="card">
          <div className="card-body p-0">
            <div style={{ padding: 16, borderBottom: "1px solid #f0f0f0" }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong>
                    {selectedCallTypeFilter
                      ? `Showing leads with "${selectedCallTypeFilter}" calls: ${filteredLeads.length} results`
                      : `Showing all leads: ${leads.length} results`}
                  </Text>
                </Col>
                {selectedCallTypeFilter && (
                  <Col>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => setSelectedCallTypeFilter(null)}
                    >
                      Show All ({leads.length})
                    </Button>
                  </Col>
                )}
              </Row>
            </div>
            <Table
              dataSource={sortLeadsByAssignedUser(filteredLeads)}
              columns={columns}
              Selection={false}
            />
          </div>
        </div>
        {/* Call Logs Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <MessageOutlined style={{ marginRight: 8 }} />
              Call Logs for {selectedLead?.name}
            </div>
          }
          open={isCallLogModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={700}
        >
          {selectedLead && (
            <div>
              <Descriptions
                column={2}
                bordered
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Descriptions.Item label="Name">
                  {selectedLead.name || "null"}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {selectedLead.email || "null"}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {selectedLead.mobileNumber || "null"}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag
                    color={
                      selectedLead.status === "assigned" ? "green" : "orange"
                    }
                  >
                    {selectedLead.status || "Unassigned"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Location" span={2}>
                  {[
                    selectedLead.city,
                    selectedLead.district,
                    selectedLead.state,
                    selectedLead.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "null"}
                </Descriptions.Item>
                <Descriptions.Item label="Assigned To" span={2}>
                  {getAssignedUserNames(selectedLead).length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {getAssignedUserNames(selectedLead).map((name, i) => (
                        <Tag
                          key={i}
                          color="blue"
                          style={{ marginInlineEnd: 0 }}
                        >
                          {name}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Title level={5}>Call History</Title>

              {/* Handle different cases for call_logs */}
              {selectedLead.call_logs === null ? (
                <div style={{ textAlign: "center", padding: 20 }}>
                  <CloseOutlined
                    style={{ fontSize: 24, color: "#ff4d4f", marginBottom: 8 }}
                  />
                  <div style={{ color: "#ff4d4f", fontStyle: "italic" }}>
                    Call logs data is null
                  </div>
                </div>
              ) : selectedLead.call_logs &&
                selectedLead.call_logs.length > 0 ? (
                <List
                  itemLayout="vertical"
                  dataSource={selectedLead.call_logs}
                  renderItem={(log, index) => (
                    <List.Item key={index}>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            icon={getCallTypeConfig(log.type).icon}
                            style={{
                              backgroundColor: getCallTypeConfig(log.type)
                                .color,
                            }}
                          />
                        }
                        title={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{log.type || "Unknown"} Call</span>
                            <Tag color={getCallTypeConfig(log.type).color}>
                              {log.type || "Unknown"}
                            </Tag>
                          </div>
                        }
                        description={
                          <div>
                            <div>
                              <CalendarOutlined />{" "}
                              {log.date ? (
                                moment(log.date).format("DD MMM YYYY HH:mm")
                              ) : (
                                <span
                                  style={{ color: "#999", fontStyle: "italic" }}
                                >
                                  Date not available
                                </span>
                              )}
                            </div>
                            {log.note && (
                              <div style={{ marginTop: 8 }}>
                                <Text strong>Note: </Text>
                                <Text>{log.note}</Text>
                              </div>
                            )}
                            {log.reminder_date && (
                              <div style={{ marginTop: 8 }}>
                                <BellOutlined />
                                <Text strong> Reminder: </Text>
                                <Text>
                                  {moment(log.reminder_date).format(
                                    "DD MMM YYYY HH:mm",
                                  )}
                                </Text>
                              </div>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: "center", padding: 20 }}>
                  <MessageOutlined
                    style={{ fontSize: 24, color: "#ccc", marginBottom: 8 }}
                  />
                  <div>No call logs available</div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Export Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <DownloadOutlined style={{ marginRight: 8 }} />
              Export Leads Data
            </div>
          }
          open={isExportModalVisible}
          onCancel={() => {
            setIsExportModalVisible(false);
            setSelectedExportUserIds([]);
            setExportUserSelectAll(true);
          }}
          footer={null}
        >
          <div style={{ marginBottom: 16 }}>
            <Text strong>Select Users</Text>
            <div
              style={{
                maxHeight: 250,
                overflowY: "auto",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                padding: 8,
                marginTop: 8,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={exportUserSelectAll}
                  onChange={(e) => {
                    setExportUserSelectAll(e.target.checked);
                    if (e.target.checked) setSelectedExportUserIds([]);
                  }}
                >
                  <strong>All Users</strong>
                </Checkbox>
              </div>

              <Divider style={{ margin: "8px 0" }} />

              {formUserOptions.map((u) => (
                <div key={u.key} style={{ marginBottom: 8 }}>
                  <Checkbox
                    checked={
                      !exportUserSelectAll &&
                      selectedExportUserIds.includes(Number(u.key))
                    }
                    onChange={(e) => {
                      setExportUserSelectAll(false);
                      if (e.target.checked) {
                        setSelectedExportUserIds((prev) => [
                          ...prev,
                          Number(u.key),
                        ]);
                      } else {
                        setSelectedExportUserIds((prev) =>
                          prev.filter((id) => id !== Number(u.key)),
                        );
                      }
                    }}
                  >
                    {u.fname} {u.lname}
                  </Checkbox>
                </div>
              ))}

              {formUserOptions.length === 0 && (
                <div style={{ color: "#999" }}>
                  No users associated with this form
                </div>
              )}
            </div>
            {!exportUserSelectAll && (
              <small style={{ color: "#888" }}>
                {selectedExportUserIds.length} user(s) selected
              </small>
            )}
          </div>

          <div style={{ textAlign: "right" }}>
            <Button
              onClick={() => setIsExportModalVisible(false)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              loading={exportLoading}
              disabled={
                leads.length === 0 ||
                (!exportUserSelectAll && selectedExportUserIds.length === 0)
              }
              onClick={() => {
                setIsExportModalVisible(false);
                handleExportExcel();
              }}
            >
              Download Excel
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default EmployeeUserCall;
